import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

import { LoginLayout } from "@src/deprecated/pages/LoginLayout";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

type SignupCompletionPendingState = {
  state: "pending";
  message: string;
};

type SignupCompletionFailedState = {
  state: "failed";
  title: string;
  description: string;
  requestId?: string;
  supportHref: string;
  signInHref?: string;
  onRetry?: () => void;
};

export type SignupCompletionPageState =
  | SignupCompletionPendingState
  | SignupCompletionFailedState;

export interface SignupCompletionPageProps {
  view: SignupCompletionPageState;
}

export const SignupCompletionPage = ({ view }: SignupCompletionPageProps) => {
  if (view.state === "pending") {
    return (
      <LoginLayout>
        <VerticalFlexbox
          alignItems="center"
          justifyContent="center"
          gap="md"
          className="w-full text-center"
        >
          <Spinner
            size="lg"
            className="text-accent"
            label="Setting up account"
          />
          <HeaderText tag="h2">Setting up your account</HeaderText>
          <Text
            role="status"
            aria-live="polite"
            aria-atomic="true"
            sizing="sm"
            appearance="subdued"
            className="max-w-sm"
          >
            {view.message}
          </Text>
        </VerticalFlexbox>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="md"
        className="w-full text-center"
      >
        <VerticalFlexbox
          alignItems="center"
          justifyContent="center"
          className="size-12 rounded-full bg-destructive/10 text-destructive"
        >
          <AlertTriangle className="size-6" />
        </VerticalFlexbox>
        <HeaderText tag="h2">{view.title}</HeaderText>
        <Text sizing="sm" appearance="subdued" className="max-w-sm">
          {view.description}
        </Text>
        {view.requestId ? (
          <VerticalFlexbox
            gap="xs"
            className="w-full max-w-sm rounded-md border border-border bg-muted/40 px-4 py-3 text-left"
          >
            <Text sizing="xs" appearance="subdued">
              Support reference
            </Text>
            <Text as="code" sizing="sm" fontWeight={600} className="font-mono">
              {view.requestId}
            </Text>
          </VerticalFlexbox>
        ) : null}
        <VerticalFlexbox gap="sm" className="w-full max-w-sm">
          <Button asChild className="w-full">
            <a href={view.supportHref}>Contact support</a>
          </Button>
          {view.signInHref ? (
            <Button asChild variant="outline" className="w-full">
              <Link to={view.signInHref}>Sign in</Link>
            </Button>
          ) : null}
          {view.onRetry ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={view.onRetry}
            >
              Retry status check
            </Button>
          ) : null}
        </VerticalFlexbox>
      </VerticalFlexbox>
    </LoginLayout>
  );
};
